/**
 * OI volume altitude band — the one place that decides which wire field is
 * allowed to fill "Alt Lower / Alt Upper".
 *
 * Verified against the live sandbox broker 2026-08-24. For one aircraft, at
 * one instant, the volume floor/ceiling arrive **three** ways:
 *
 *   telemetry.alt_lower_wgs84_m                             868.7932071685791
 *   telemetry.operational_volume…properties.min_altitude     { meters: 868.7932071685791, datum: "W84" }
 *   RTM  operational_intent[].volume.altitude_lower          { value: 868.7932071685791, reference: "W84", units: "M" }
 *
 * All three agree to 13 decimals. None of them was read by this app until
 * now: the OI panel derived its "WGS84" band from `waypoints[*][2]` instead,
 * which is **AGL** (400 / 700 m on the same message). The difference is the
 * terrain elevation under the polygon — a constant 468.79 m on that flight —
 * so the panel displayed AGL under a WGS84 label.
 *
 * Waypoint altitudes are still useful, but they are AGL and belong to
 * `agl_ceiling_ft`. They must never reach `altitude_floor_m` /
 * `altitude_ceiling_m` again; that is the bug this module exists to prevent.
 *
 * Priority:
 *   1. `operational_volume` feature properties — the only representation that
 *      ships an explicit `datum`, so it is the only one we can label honestly.
 *   2. `alt_lower_wgs84_m` / `alt_upper_wgs84_m` — datum inferred from the
 *      field name (a direct name match, the same bar every other mapping in
 *      this codebase has to clear).
 *   3. Nothing → nulls, and the UI renders "—".
 */

/** Volume floor/ceiling in metres, plus the datum they are expressed in.
 *  `datum` is null when the source didn't declare one AND the field name
 *  didn't imply one — in that case the UI must not claim a datum. */
export interface OiAltitudeBand {
  floorM: number | null;
  ceilingM: number | null;
  datum: string | null;
}

export const EMPTY_BAND: OiAltitudeBand = { floorM: null, ceilingM: null, datum: null };

function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Normalise the datum spelling. The broker says `"W84"`; ASTM/F3411 and the
 *  RTM API both also use `"W84"` (RTM calls the key `reference`). Anything
 *  else is passed through verbatim rather than coerced — an unrecognised
 *  datum is information, not noise. */
function normaliseDatum(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (s === '') return null;
  return s.toUpperCase() === 'W84' || s.toUpperCase() === 'WGS84' ? 'W84' : s;
}

/** One `{ meters, datum }` altitude object as published on
 *  `operational_volume.features[].properties.{min,max}_altitude`. */
function readAltitudeObject(v: unknown): { m: number | null; datum: string | null } {
  if (v == null || typeof v !== 'object') return { m: null, datum: null };
  const o = v as Record<string, unknown>;
  // `meters` is the broker's key; `value` is RTM's, accepted so the same
  // helper can read a declaration fetched over REST.
  return { m: num(o.meters ?? o.value), datum: normaliseDatum(o.datum ?? o.reference) };
}

/**
 * Read the band from an `operational_volume` FeatureCollection.
 *
 * Scans **every** feature, not just `features[0]`. The live RTM declaration
 * for the same flight carries 15 time-sliced `operational_intent` volumes, so
 * a single-feature read would silently describe one slice as the whole
 * operation. Floor = lowest min across slices, ceiling = highest max.
 */
export function readOperationalVolumeBand(ov: unknown): OiAltitudeBand {
  if (ov == null || typeof ov !== 'object') return EMPTY_BAND;
  const features = (ov as { features?: unknown }).features;
  if (!Array.isArray(features) || features.length === 0) return EMPTY_BAND;

  let floor = Number.POSITIVE_INFINITY;
  let ceiling = Number.NEGATIVE_INFINITY;
  const datums = new Set<string>();

  for (const f of features) {
    if (f == null || typeof f !== 'object') continue;
    const props = (f as { properties?: unknown }).properties;
    if (props == null || typeof props !== 'object') continue;
    const p = props as Record<string, unknown>;

    const lo = readAltitudeObject(p.min_altitude);
    const hi = readAltitudeObject(p.max_altitude);
    if (lo.m != null && lo.m < floor) floor = lo.m;
    if (hi.m != null && hi.m > ceiling) ceiling = hi.m;
    if (lo.datum) datums.add(lo.datum);
    if (hi.datum) datums.add(hi.datum);
  }

  const floorM = Number.isFinite(floor) ? floor : null;
  const ceilingM = Number.isFinite(ceiling) ? ceiling : null;
  if (floorM == null && ceilingM == null) return EMPTY_BAND;

  // Mixed datums across slices would make the min/max incomparable. Keep the
  // numbers (they are all metres) but refuse to label them, so the UI shows
  // the band without asserting a datum nobody guaranteed.
  const datum = datums.size === 1 ? [...datums][0]! : null;
  return { floorM, ceilingM, datum };
}

/**
 * Read the OI volume band from a `telemetry` or `oi/declaration` payload.
 *
 * Never falls back to waypoint altitudes — see the module header.
 */
export function readOiAltitudeBand(payload: unknown): OiAltitudeBand {
  if (payload == null || typeof payload !== 'object') return EMPTY_BAND;
  const p = payload as Record<string, unknown>;

  const fromVolume = readOperationalVolumeBand(p.operational_volume);
  if (fromVolume.floorM != null || fromVolume.ceilingM != null) return fromVolume;

  const floorM = num(p.alt_lower_wgs84_m);
  const ceilingM = num(p.alt_upper_wgs84_m);
  if (floorM == null && ceilingM == null) return EMPTY_BAND;

  // Datum comes from the field name here, not from a declared value. That is
  // weaker evidence than case 1, but `alt_lower_wgs84_m` is as explicit as a
  // field name gets, and the live values match the datum-carrying source
  // exactly.
  return { floorM, ceilingM, datum: 'W84' };
}

/** Human label for a datum code. `null` → no claim. */
export function datumLabel(datum: string | null): string | null {
  if (!datum) return null;
  return datum === 'W84' ? 'WGS84' : datum;
}
